import React from 'react';
import './EVFilters.scss';
import AhmIcon from 'components/Global/AhmIcon/AhmIcon';
import SwiperCore, { A11y, Mousewheel, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { IStationFilter } from 'interfaces/chargingLocator';
import { trackEvent, checkURL } from 'helpers/Analytics';
import { appBreakpoints } from 'theme/App.breakpoints';
import { getZipcode } from 'helpers/Zipgate';

interface IEVFilters {
  filters: IStationFilter[];
  onFilterChange: (val: IStationFilter) => void;
}

SwiperCore.use([Navigation, A11y, Mousewheel]);

const swipperNavigation = {
  nextEl: '.swiper-button-next',
  prevEl: '.swiper-button-prev',
  enabled: true,
};

const isHonda = process.env.REACT_APP_THEME === 'honda';
const filterStyle = isHonda ? 'ev-filters__honda-filter' : 'ev-filters__filter';

const swiperBreakpoints = {
  360: {
    slidesPerView: 3,
    enabled: true,
    spaceBetween: 16,
  },
  580: {
    slidesPerView: 5,
    enabled: false,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportMedium as number]: {
    slidesPerView: 3,
    enabled: true,
    spaceBetween: 10,
  },
  800: {
    slidesPerView: 3,
    enabled: true,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportMediumMid as number]: {
    slidesPerView: 4,
    enabled: true,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportLarge as number]: {
    slidesPerView: 5,
    enabled: false,
    spaceBetween: 16,
  },
};

const swiperBreakpointsHonda = {
  360: {
    slidesPerView: 3,
    enabled: true,
    spaceBetween: 16,
  },
  580: {
    slidesPerView: 5,
    enabled: false,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportMedium as number]: {
    slidesPerView: 2,
    enabled: true,
    spaceBetween: 14,
  },
  815: {
    slidesPerView: 3,
    enabled: true,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportMediumMid as number]: {
    slidesPerView: 4,
    enabled: true,
    spaceBetween: 16,
  },
  [appBreakpoints.values?.viewportLarge as number]: {
    slidesPerView: 4,
    enabled: true,
    spaceBetween: 16,
  },
  1060: {
    slidesPerView: 5,
    enabled: false,
    spaceBetween: 16,
  },
};

const EVFilters = ({ filters, onFilterChange }: IEVFilters): JSX.Element => {
  function handleClick(filter: IStationFilter) {
    const trackingObject = isHonda
      ? { Page: { page_name: '' } }
      : window.Acr.trackingObject || {};
    const dspToken = sessionStorage.getItem('dsp_token');
    onFilterChange(filter);
    if (!isHonda) {
      if (filter.isSelected) {
        trackEvent({
          label: `csl:filter:${filter.title}`,
          category: {
            includePageName: false,
            text: 'csl:filter',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      } else {
        trackEvent({
          label: `csl:filter:${filter.title}:deselect`,
          category: {
            includePageName: false,
            text: 'csl:filter',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      }
    } else {
      if (filter.isSelected) {
        trackEvent({
          label: `csl:filter:${filter.title}`,
          category: {
            includePageName: false,
            text: 'csl:filter',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      } else {
        trackEvent({
          label: `csl:filter:${filter.title}:deselect`,
          category: {
            includePageName: false,
            text: 'csl:filter',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      }
    }
  }

  return (
    <div className="ev-filters">
      <Swiper
        className={`ev-filters__swiper prioritize`}
        slidesPerView="auto"
        mousewheel={true}
        navigation={swipperNavigation}
        spaceBetween={16}
        a11y={{ enabled: false }}
        allowTouchMove={false}
        breakpoints={isHonda ? swiperBreakpointsHonda : swiperBreakpoints}
      >
        {filters.map((filter: IStationFilter, index: number) => (
          <SwiperSlide key={index} className={`ev-filters__slide prioritize`}>
            <button
              className={`${filterStyle} ahm-label4 ${
                filter.isSelected ? 'selected' : ''
              }`}
              onClick={() => handleClick(filter)}
            >
              {filter.iconUrl && (
                <img
                  className="ev-filters__filter-icon"
                  alt={
                    filter.isSelected
                      ? filter.iconSelectedStateTextEquivalent || 'icon'
                      : filter.iconTextEquivalent || 'icon'
                  }
                  src={
                    filter.isSelected
                      ? filter.iconSelectedStateUrl || ''
                      : filter.iconUrl || ''
                  }
                ></img>
              )}
              {filter.title}
            </button>
          </SwiperSlide>
        ))}
        <div className={`swiper-controls`}>
          <div
            className={`swiper-button-prev ev-filters__navigation-button prioritize`}
          >
            <AhmIcon iconName="arrowRight" />
          </div>
          <div
            className={`swiper-button-next ev-filters__navigation-button prioritize`}
          >
            <AhmIcon iconName="arrowRight" />
          </div>
        </div>
      </Swiper>
    </div>
  );
};

export default EVFilters;
